/**
 * 复制页面配置工具函数
 * 支持开始页和中间页之间的配置复制
 */

import { shortFormAddList } from '@/views/wheelOfFortune/constant/options';
import Vue from 'vue';

type CopyDirection = 'startToMiddle' | 'middleToStart';

interface Settings {
  middle?: any;
  start?: any;
  typesetting?: any;
  rules?: any;
  logo?: any;
  texts?: any;
  styles?: {
    styleMap?: Record<string, any>;
  };
}

/**
 * 复制页面配置
 * @param settings 配置对象
 * @param direction 复制方向：'startToMiddle' 从开始页复制到中间页，'middleToStart' 从中间页复制到开始页
 */
export function copyPageConfig(settings: Settings, direction: CopyDirection = 'startToMiddle'): void {
  if (!settings) {
    return;
  }

  const isStartToMiddle = direction === 'startToMiddle';

  // 复制 typesetting 配置
  if (settings.typesetting) {
    const typesettingFields = [
      { start: 'imgUrl', middle: 'middleImgUrl' },
      { start: 'bgImgUrl', middle: 'middleBgImgUrl' },
      { start: 'imgPosition', middle: 'middleImgPosition' },
      { start: 'bgbox', middle: 'middleBgbox' },
      { start: 'bgboxColor', middle: 'middleBgboxColor' },
      { start: 'leftAdImage', middle: 'middleLeftAdImage' },
      { start: 'rightAdImage', middle: 'middleRightAdImage' },
    ];

    typesettingFields.forEach(({ start, middle }) => {
      const sourceKey = isStartToMiddle ? start : middle;
      const targetKey = isStartToMiddle ? middle : start;
      if (settings.typesetting![sourceKey] !== undefined) {
        // 使用 Vue.set 确保新添加的属性是响应式的
        Vue.set(settings.typesetting!, targetKey, settings.typesetting![sourceKey]);
      }
    });
  }

  // 复制 rules 配置
  if (settings.rules) {
    const rulesFields = [
      { start: 'closeColor', middle: 'middleCloseColor' },
      { start: 'closeButtonConfig', middle: 'middleCloseButtonConfig' },
      { start: 'mobilePictureShow', middle: 'middleMobilePictureShow' },
      { start: 'mobileRatio', middle: 'middleMobileRatio' },
      { start: 'adapterText', middle: 'middleAdapterText' },
      { start: 'inputType', middle: 'middleInputType' },
      { start: 'inputBorderStyle', middle: 'middleInputBorderStyle' },
      { start: 'inputBorderColor', middle: 'middleInputBorderColor' },
      { start: 'inputBorderRadius', middle: 'middleInputBorderRadius' },
      { start: 'inputBackgroundShow', middle: 'middleInputBackgroundShow' },
      { start: 'inputBackground', middle: 'middleInputBackground' },
      { start: 'inputColor', middle: 'middleInputColor' },
      { start: 'inputMarginTop', middle: 'middleInputMarginTop' },
      { start: 'inputMarginBottom', middle: 'middleInputMarginBottom' },
      { start: 'inputMarginLeft', middle: 'middleInputMarginLeft' },
      { start: 'inputMarginRight', middle: 'middleInputMarginRight' },
      { start: 'optionColumns', middle: 'middleOptionColumns' },
      { start: 'optionColor', middle: 'middleOptionColor' },
      { start: 'optionTitleColor', middle: 'middleOptionTitleColor' },
      { start: 'optionTextColor', middle: 'middleOptionTextColor' },
      { start: 'optionFontFamily', middle: 'middleOptionFontFamily' },
      { start: 'optionTitleFontSize', middle: 'middleOptionTitleFontSize' },
      { start: 'optionFontSize', middle: 'middleOptionFontSize' },
      { start: 'optionBackground', middle: 'middleOptionBackground' },
      { start: 'optionMarginTop', middle: 'middleOptionMarginTop' },
      { start: 'optionMarginBottom', middle: 'middleOptionMarginBottom' },
      { start: 'optionMarginLeft', middle: 'middleOptionMarginLeft' },
      { start: 'optionMarginRight', middle: 'middleOptionMarginRight' },
      { start: 'dropdownTitleColor', middle: 'middleDropdownTitleColor' },
      { start: 'dropdownTitleFontSize', middle: 'middleDropdownTitleFontSize' },
      { start: 'dropdownTitleFontFamily', middle: 'middleDropdownTitleFontFamily' },
      { start: 'dropdownBorderStyle', middle: 'middleDropdownBorderStyle' },
      { start: 'dropdownBorderColor', middle: 'middleDropdownBorderColor' },
      { start: 'dropdownBorderRadius', middle: 'middleDropdownBorderRadius' },
      { start: 'dropdownBackgroundType', middle: 'middleDropdownBackgroundType' },
      { start: 'dropdownBackgroundColor', middle: 'middleDropdownBackgroundColor' },
      { start: 'dropdownOptionTextColor', middle: 'middleDropdownOptionTextColor' },
      { start: 'dropdownMarginTop', middle: 'middleDropdownMarginTop' },
      { start: 'dropdownMarginBottom', middle: 'middleDropdownMarginBottom' },
      { start: 'dropdownMarginLeft', middle: 'middleDropdownMarginLeft' },
      { start: 'dropdownMarginRight', middle: 'middleDropdownMarginRight' },
      { start: 'countDownShow', middle: 'middleCountDownShow' },
      { start: 'buttonAction', middle: 'middleButtonAction' },
      { start: 'openUrl', middle: 'middleOpenUrl' },
      { start: 'privacyPosition', middle: 'middlePrivacyPosition', defaultValue: 'form' },
      { start: 'mobilePictureShow', middle: 'middleMobilePictureShow' },
    ];

    rulesFields.forEach(({ start, middle, defaultValue }) => {
      const sourceKey = isStartToMiddle ? start : middle;
      const targetKey = isStartToMiddle ? middle : start;
      if (settings.rules![sourceKey] !== undefined) {
        // 使用 Vue.set 确保新添加的属性是响应式的
        Vue.set(settings.rules!, targetKey, settings.rules![sourceKey] ?? defaultValue);
      }
    });
  }

  // 复制 logo 配置
  if (settings.logo) {
    if (isStartToMiddle) {
      if (settings.logo.imgUrl !== undefined) {
        // 使用 Vue.set 确保新添加的属性是响应式的
        Vue.set(settings.logo, 'middleImgUrl', settings.logo.imgUrl);
      }
    } else {
      if (settings.logo.middleImgUrl !== undefined) {
        // 使用 Vue.set 确保新添加的属性是响应式的
        Vue.set(settings.logo, 'imgUrl', settings.logo.middleImgUrl);
      }
    }
  }

  // 复制 texts 配置
  if (settings.texts) {
    const textsFields = [
      { start: 'textAreaImgUrl', middle: 'middleTextAreaImgUrl' },
      { start: 'topTitle', middle: 'middleTopTitle' },
      { start: 'topSubTitle', middle: 'middleTopSubTitle' },
      { start: 'mainButton', middle: 'middleMainButton' },
      { start: 'subButton', middle: 'middleSubButton' },
      { start: 'activityTitle', middle: 'middleActivityTitle' },
    ];

    textsFields.forEach(({ start, middle }) => {
      const sourceKey = isStartToMiddle ? start : middle;
      const targetKey = isStartToMiddle ? middle : start;
      if (settings.texts![sourceKey] !== undefined) {
        // 使用 Vue.set 确保新添加的属性是响应式的
        Vue.set(settings.texts!, targetKey, settings.texts![sourceKey]);
      }
    });
    // 注意：couponPrompt 和 firstStepButton 在中间页和开始页共用，不需要复制
  }

  // 复制样式配置 (styleMap)
  if (settings.styles?.styleMap) {
    const styleMap = settings.styles.styleMap;

    // 需要复制的样式键列表
    const styleKeysToCopy = [
      'cartx-b-wheel-container',
      'cartx-b-show',
      'cartx-b-title',
      'cartx-b-subtitle',
      'cartx-b-privacy',
      'cartx-b-button',
      'cartx-b-thanks',
      'cartx-b-input',
      'cartx-b-activity',
      'cartx-b-show .cartx-b-button',
      'cartx-b-time-item'
    ];

    // 复制指定的样式键
    styleKeysToCopy.forEach((key: string) => {
      // 处理包含空格的复合选择器（如 'cartx-b-show .cartx-b-button'）
      // 需要在每个部分后面添加或移除 __middle
      let sourceKey: string;
      let targetKey: string;

      if (key.includes(' ')) {
        // 复合选择器：处理每个部分
        const parts = key.split(' ');
        if (isStartToMiddle) {
          sourceKey = key;
          targetKey = parts.map(part => `${part}__middle`).join(' ');
        } else {
          sourceKey = parts.map(part => `${part}__middle`).join(' ');
          targetKey = key;
        }
      } else {
        // 简单选择器：原有逻辑
        sourceKey = isStartToMiddle ? key : `${key}__middle`;
        targetKey = isStartToMiddle ? `${key}__middle` : key;
      }

      if (styleMap[sourceKey]) {
        if (!styleMap[targetKey]) {
          // 使用 Vue.set 确保新添加的属性是响应式的
          Vue.set(styleMap, targetKey, {});
        }
        // 使用 Vue.set 逐个复制属性，确保响应式
        const sourceStyle = styleMap[sourceKey];
        const targetStyle = styleMap[targetKey];
        Object.keys(sourceStyle).forEach((propKey: string) => {
          Vue.set(targetStyle, propKey, sourceStyle[propKey]);
        });
      }
    });

    // 复制表单相关样式（动态匹配）
    const formStyleKeys = Object.keys(styleMap).filter((key: string) => {
      const baseKey = key.replace('__middle', '');
      return (
        (baseKey.includes('form') || baseKey.includes('input') || baseKey.includes('button')) &&
        (isStartToMiddle ? !key.includes('__middle') : key.includes('__middle'))
      );
    });

    formStyleKeys.forEach((key: string) => {
      if (isStartToMiddle) {
        const middleKey = key + '__middle';
        if (!styleMap[middleKey]) {
          // 使用 Vue.set 确保新添加的属性是响应式的
          Vue.set(styleMap, middleKey, {});
        }
        // 使用 Vue.set 逐个复制属性，确保响应式
        const sourceStyle = styleMap[key];
        const targetStyle = styleMap[middleKey];
        Object.keys(sourceStyle).forEach((propKey: string) => {
          Vue.set(targetStyle, propKey, sourceStyle[propKey]);
        });
      } else {
        const startKey = key.replace('__middle', '');
        if (!styleMap[startKey]) {
          // 使用 Vue.set 确保新添加的属性是响应式的
          Vue.set(styleMap, startKey, {});
        }
        // 使用 Vue.set 逐个复制属性，确保响应式
        const sourceStyle = styleMap[key];
        const targetStyle = styleMap[startKey];
        Object.keys(sourceStyle).forEach((propKey: string) => {
          Vue.set(targetStyle, propKey, sourceStyle[propKey]);
        });
      }
    });

    // 注意：表单字段列表（start 字段）中间页和开始页共享，不需要复制
  }

  // 复制表单字段列表
  if (isStartToMiddle) {
    if (settings.rules?.stepDisplay === true) {
      // 先保存原始的 start 数组，避免在修改后无法获取正确的值
      const originalStart = settings?.start || [];
      Vue.set(settings, 'start', originalStart.slice(0, 1) as any);
      Vue.set(settings, 'middle', originalStart.slice(1, 2) as any);
      // Vue.set 不支持点号路径，需要直接设置 rules 对象的属性
      // if (settings.rules) {
      //   Vue.set(settings.rules, 'stepDisplay', false);
      // }
    } else {
      // 过滤出 Mobile Number 类型的选项
      const mobileNumberOptions = shortFormAddList.filter(item => item.type === 'Mobile Number');
      // 深拷贝数组，避免引用问题
      const mobileNumberOptionsCopy = JSON.parse(JSON.stringify(
        mobileNumberOptions.map((item: any) => ({
          ...item,
          necessary: false,
        }))
      ));
      // 使用 Vue.set 确保响应式
      Vue.set(settings, 'middle', mobileNumberOptionsCopy);
    }
  } else {
    Vue.set(settings, 'start', settings?.middle as any);
  }
}

/**
 * 从开始页复制配置到中间页（便捷方法）
 */
export function copyStartToMiddle(settings: Settings): void {
  copyPageConfig(settings, 'startToMiddle');
}

/**
 * 从中间页复制配置到开始页（便捷方法）
 */
export function copyMiddleToStart(settings: Settings): void {
  copyPageConfig(settings, 'middleToStart');
}
